import React, { memo, useState } from 'react';
import { View } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import { ListContainer, ThemeList } from '@redlink/components/organisms';
import { ThemeEditPage } from '@redlink/components/pages';
import { useLocale } from '@redlink/assets/locale';
import {
  useMutation,
  useQuery,
  getPageThemeQuery,
  removeThemeQuery,
  setThemeAsDefaultQuery,
} from '@redlink/services';
import { useDialog } from '@redlink/components/molecules';
import {
  freshCacheForRemoveItem,
  freshCacheForSetAsDefaultItem,
} from './cache';
import {
  LoadingContainer,
  WebScrollView,
  IconEdit,
} from '@redlink/components/atoms';

export const ThemePage = memo(() => {
  const { styles, colors } = useStyle();
  const { dictionary } = useLocale();
  const [newThemeIsVisible, setNewThemeIsVisible] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(null);
  const { showConfirmDialog } = useDialog();

  const {
    loading: getPageThemesIsBusy,
    data: { Themes = [] },
  } = useQuery(getPageThemeQuery);

  const [removeTheme, { loading: removeThemeIsBusy }] = useMutation(
    removeThemeQuery,
    {
      done: () => {},
      update: freshCacheForRemoveItem(),
    }
  );

  const [setThemeAsDefault, { loading: setThemeAsDefaultIsBusy }] = useMutation(
    setThemeAsDefaultQuery,
    {
      done: () => {},
      update: freshCacheForSetAsDefaultItem(),
    }
  );

  return (
    <View style={styles.container}>
      <WebScrollView>
        <ListContainer
          isFetching={getPageThemesIsBusy}
          title={dictionary.chooseYourTheme}
          containerContainerStyle={styles.categoriesContainerStyle}
          actions={[
            {
              title: dictionary.makeYourSpecialTheme,
              variant: 'primary',
              icon: <IconEdit color={colors.white} />,
              onPress: () => {
                setCurrentTheme(null);
                setNewThemeIsVisible(true);
              },
            },
          ]}
        >
          <LoadingContainer
            hasOverlay={false}
            isBusy={removeThemeIsBusy || setThemeAsDefaultIsBusy}
          >
            <ThemeList
              data={Themes}
              onPress={(item, index) => {
                setCurrentTheme(item);
                setNewThemeIsVisible(true);
              }}
              contentContainerStyle={styles.contentContainerStyle}
              onEditPress={(item) => {
                setCurrentTheme(item);
                setNewThemeIsVisible(true);
              }}
              onSetAsDefaultPress={(item) => {
                setCurrentTheme(item);
                setThemeAsDefault({ id: item.id });
              }}
              onDeletePress={(item) => {
                showConfirmDialog({
                  message: dictionary.deleteConfirmMessage,
                  onConfirm: () => {
                    setCurrentTheme(item);
                    removeTheme({ id: item.id });
                  },
                });
              }}
            />
          </LoadingContainer>
        </ListContainer>
      </WebScrollView>
      <ThemeEditPage
        isVisible={newThemeIsVisible}
        {...(currentTheme && { data: currentTheme })}
        onDismiss={() => setNewThemeIsVisible(false)}
      />
    </View>
  );
});

const useStyle = createUseStyle(({ colors, size }) => ({
  container: {
    flex: 1,
  },
  contentContainerStyle: {
    paddingVertical: 8,
  },
  categoriesContainerStyle: {
    marginTop: 16,
  },
}));
